
import { isGroupChat, makeSelectNameForDisplay, selectChatById } from "./redux/TeamInboxSelectors";
import RowStack from "@common/components/common/row-stack/RowStack";
import { openTeamInboxChatThunk } from "./redux/TeamInboxThunks";
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import { VolumeOff as VolumeOffIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import AckIcon from "./ack-Icon";
import { NotificationAndLastMessageView } from "./notification-and-last-message-view";
import { authorIdOf, getChatTimestamp, timestampToDateLabel } from './team-inbox-helpers';
import SourceBadge from "./source-badge";

type ChatDisplayRowProps = {
    itemId: string;
}

function ChatDisplayRow({ itemId }: ChatDisplayRowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const waChat = useAppSelector(state => selectChatById(state, itemId));


    const isActive = useAppSelector(state => (state.TeamInboxReducer?.chatViewMode?.chatId ?? null) === itemId);
    const hasUnreadMessages = waChat?.unreadCount > 0;
    const showMuteIcon = waChat?.muted ?? false;
    // The declared row: `author` is a jid string (absent on an incoming DM).
    const authorId = authorIdOf(waChat?.lastMessage);
    // The row names the sender `senderName`; a live listener message carries `notifyName`.
    const notifyName = waChat?.lastMessage?.senderName ?? waChat?.lastMessage?.notifyName;

    // Memoize the selector to prevent creating new selector on every render
    const selectSenderName = useMemo(
        () => makeSelectNameForDisplay(authorId ?? '', notifyName),
        [authorId, notifyName]
    );
    const senderNameIngroup = useAppSelector(selectSenderName);

    const styles = {
        container: {
            backgroundColor: isActive
                ? 'var(--wa-row-selected-bg)'
                : "transparent",
            "&:hover": {
                backgroundColor: isActive
                    ? 'var(--wa-row-selected-bg)'
                    : 'var(--wa-row-hover-bg)',
            },
            // WhatsApp Web draws a hairline that starts after the avatar; a
            // full-width divider is the accepted fallback given the flat DOM.
            borderBottom: '1px solid var(--wa-divider)'
        },
        chatName: {
            fontWeight: hasUnreadMessages ? 600 : 400,
        },
        timestamp: {
            color: hasUnreadMessages ? 'var(--wa-unread-badge)' : 'var(--wa-secondary-text)',
            fontWeight: hasUnreadMessages ? 600 : 400,
        },
        senderNameIngroup: {
            fontWeight: hasUnreadMessages ? 600 : 400,
            color: hasUnreadMessages ? 'var(--wa-title-text)' : 'var(--wa-secondary-text)',
        },
    };

    const handleChatDisplayRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.nativeEvent.stopImmediatePropagation();
        e.stopPropagation();

        if (isActive) {
            return;
        }

        dispatch(openTeamInboxChatThunk({ chatId: itemId }));
    }

    // WhatsApp Web unread pill: sits in the right column under the timestamp
    // (not over the avatar). Filled green circle, white bold count.
    const renderUnreadBadge = () => {
        if (!hasUnreadMessages) return null;
        return (
            <Box
                sx={{
                    backgroundColor: 'var(--wa-unread-badge)',
                    borderRadius: '999px',
                    minWidth: 20,
                    height: 20,
                    px: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#ffffff',
                        lineHeight: 1,
                    }}
                >
                    {waChat.unreadCount > 99 ? '99+' : waChat.unreadCount}
                </Typography>
            </Box>
        );
    };

    const renderMessageContent = () => {
        return (
            <>
                {
                    !waChat?.lastMessage && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                            {t('teamInbox.lastMessageUnavailable')}
                        </Typography>
                    )
                }

                {waChat?.lastMessage && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {waChat?.lastMessage?.fromMe && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                                <AckIcon ackValue={waChat?.lastMessage.ack} shouldApplyMarginRight={true} />
                            </Box>
                        )}

                        {(isGroupChat(waChat) && waChat?.lastMessage?.type !== "gp2" && senderNameIngroup) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.85rem',
                                        mr: 0.5,
                                        ...styles.senderNameIngroup,
                                    }}
                                    noWrap
                                >
                                    {`${senderNameIngroup}:`}
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                                flex: 1,
                            }}
                        >
                            <NotificationAndLastMessageView
                                waChatId={itemId}
                            />
                        </Box>

                        <RowStack sx={{ ml: 'auto', alignItems: 'center', flexShrink: 0 }}>
                            {showMuteIcon && (
                                <VolumeOffIcon
                                    sx={{
                                        fontSize: "0.9rem",
                                        color: 'var(--wa-meta-text)',
                                        mx: 0.5
                                    }}
                                />
                            )}
                        </RowStack>
                    </Box>
                )}
            </>
        );
    };

    return (
        <RowStack
            key={itemId}
            sx={{
                height: "72px",
                padding: "8px 16px 8px 8px",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                ...styles.container,
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => handleChatDisplayRowClick(e)}
        >
            <Box position="relative" sx={{ marginInlineEnd: 1.5 }}>
                <ContactAvatar
                    size={49}
                    textSize={16}
                    contactId={itemId}
                />
            </Box>

            <Stack
                sx={{
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    overflow: "hidden",
                    maxWidth: "calc(100% - 65px)"
                }}
            >
                <RowStack sx={{ justifyContent: 'space-between', width: "100%" }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: 'calc(100% - 70px)' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: '17px',
                                color: 'var(--wa-title-text)',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                ...styles.chatName,
                            }}
                            title={waChat?.name}
                        >
                            {waChat?.name}
                        </Typography>
                        {waChat?.source && <SourceBadge source={waChat.source} size="small" />}
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '12px',
                            marginInlineStart: '4px',
                            minWidth: '50px',
                            width: 'auto',
                            flexShrink: 0,
                            textAlign: 'end',
                            whiteSpace: 'nowrap',
                            ...styles.timestamp,
                        }}
                    >
                        {timestampToDateLabel(getChatTimestamp(waChat), true)}
                    </Typography>
                </RowStack>

                <RowStack
                    sx={{
                        alignItems: 'center',
                        marginTop: "2px",
                        maxWidth: '100%',
                        gap: 0.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        {renderMessageContent()}
                    </Box>
                    {renderUnreadBadge()}
                </RowStack>
            </Stack>
        </RowStack>
    );
}

export default React.memo(ChatDisplayRow);